import { Result, Spin } from "antd";
import _ from "lodash";
import React from "react";
import { useNotification } from "../../contexts/Notifications";
import {
  useGetAllProjectsQuery,
  useUpdateIsCompletedProjectConfigMutation,
  useUpdateIsTicksheetProjectConfigMutation,
  useUpdateIsTrackingProjectConfigMutation,
  useUpdateProjectDisplayNameMutation,
  useUpdateProjectDivisionMutation,
  useUpdateProjectEndDateMutation,
  useUpdateProjectReceivedOnMutation,
  useUpdateProjectStartDateMutation,
} from "../../generated/types";
import { ProjectsTable } from "./ProjectsTable";

export type Project = {
  customer: string;
  project: string;
  displayName: string | undefined;
  defaultLanguage: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  receivedOn?: Date | undefined;
  isCompleted?: boolean;
  isTracking?: boolean;
  isTicksheetProject?: boolean;
  division?: string;
  workingDays?: number[] | null;
};

export const ProjectsTableContainer = () => {
  const { data, loading, error, refetch } = useGetAllProjectsQuery();

  const projects = React.useMemo(() => {
    if (!data) {
      return [];
    }
    const unsortedProjects = data.projects.map((p) => ({
      customer: p.customer,
      project: p.project,
      displayName: p.config.displayName ?? undefined,
      defaultLanguage: p.config.defaultLanguage,
      startDate: p.config.startDate,
      endDate: p.config.endDate,
      receivedOn: p.config.receivedOn,
      isCompleted: p.config.isCompleted ?? false,
      isTracking: p.config.isTracking ?? false,
      isTicksheetProject: p.config.isTicksheetProject ?? false,
      division: p.config.division ?? undefined,
      workingDays: p.config.workingDays ?? null,
    }));

    return _.sortBy(unsortedProjects, ["customer", "project"]);
  }, [data]);

  const notify = useNotification();

  const [updateProjectStartDate] = useUpdateProjectStartDateMutation({
    onCompleted: (updateResult) =>
      notify(
        `${updateResult.updateProjectStartDate.project} project start date updated successfully!`,
        "success",
      ),
    onError: (err) =>
      notify(`Failed to update project start date! ${err.message}`, "error"),
  });

  const [updateProjectEndDate] = useUpdateProjectEndDateMutation({
    onCompleted: (updateResult) =>
      notify(
        `${updateResult.updateProjectEndDate.project} project end date updated successfully!`,
        "success",
      ),
    onError: (err) =>
      notify(`Failed to update project end date! ${err.message}`, "error"),
  });

  const [updateIsTrackingProjectConfig] =
    useUpdateIsTrackingProjectConfigMutation({
      onCompleted: (updateResult) => {
        notify(
          `${updateResult.updateIsTrackingProjectConfig.project} tracking status updated successfully!`,
          "success",
        );
      },
      onError: (err) =>
        notify(`Failed to update tracking status! ${err.message}`, "error"),
    });

  const [updateIsTicksheetProjectConfig] =
    useUpdateIsTicksheetProjectConfigMutation({
      onCompleted: (updateResult) => {
        notify(
          `${updateResult.updateIsTicksheetProjectConfig.project} tick sheet status updated successfully!`,
          "success",
        );
      },
      onError: (err) =>
        notify(`Failed to update tick sheet status! ${err.message}`, "error"),
    });

  const [updateIsCompletedProjectConfig] =
    useUpdateIsCompletedProjectConfigMutation({
      onCompleted: (updateResult) => {
        notify(
          `${updateResult.updateIsCompletedProjectConfig.project} completion status updated successfully!`,
          "success",
        );
      },
      onError: (err) =>
        notify(`Failed to update completion status! ${err.message}`, "error"),
    });

  const [updateProjectDivision] = useUpdateProjectDivisionMutation({
    onCompleted: (updateResult) => {
      notify(
        `${updateResult.updateProjectDivision.project} division updated successfully!`,
        "success",
      );
      refetch();
    },
    onError: (err) =>
      notify(`Failed to update division! ${err.message}`, "error"),
  });

  const [updateProjectDisplayName] = useUpdateProjectDisplayNameMutation({
    onCompleted: (updateResult) => {
      notify(
        `${updateResult.updateProjectDisplayName.project} display name updated successfully!`,
        "success",
      );
    },
    onError: (err) =>
      notify(`Failed to update display name! ${err.message}`, "error"),
  });

  const [updateProjectReceivedOn] = useUpdateProjectReceivedOnMutation({
    onCompleted: (updateResult) => {
      notify(
        `${updateResult.updateProjectReceivedOn.project} received on updated successfully!`,
        "success",
      );
    },
  });

  if (error) {
    return (
      <Result
        status="error"
        title="Error projects table"
        subTitle="Please refresh, try later again or escalate to the support team"
      />
    );
  }

  return (
    <div style={{ height: "100%" }}>
      <Spin spinning={loading}>
        <ProjectsTable
          projects={projects}
          onCellValueChange={async (row, changedField) => {
            if (changedField === "displayName") {
              await updateProjectDisplayName({
                variables: {
                  input: {
                    customer: row.customer,
                    project: row.project,
                    displayName: row.displayName ?? "",
                  },
                },
              });
            }
            if (changedField === "isCompleted") {
              await updateIsCompletedProjectConfig({
                variables: {
                  input: {
                    customer: row.customer,
                    project: row.project,
                    isCompleted: row.isCompleted ?? false,
                  },
                },
              });
            }
            if (changedField === "isTracking") {
              await updateIsTrackingProjectConfig({
                variables: {
                  input: {
                    customer: row.customer,
                    project: row.project,
                    isTracking: row.isTracking ?? false,
                  },
                },
              });
            }
            if (changedField === "isTicksheetProject") {
              await updateIsTicksheetProjectConfig({
                variables: {
                  input: {
                    customer: row.customer,
                    project: row.project,
                    isTicksheetProject: row.isTicksheetProject ?? false,
                  },
                },
              });
            }
            if (changedField === "division") {
              await updateProjectDivision({
                variables: {
                  input: {
                    customer: row.customer,
                    project: row.project,
                    division: row.division ?? null,
                  },
                },
              });
            }
            if (changedField === "startDate") {
              await updateProjectStartDate({
                variables: {
                  customer: row.customer,
                  project: row.project,
                  startDate: row.startDate,
                },
              });
            } else if (changedField === "endDate") {
              await updateProjectEndDate({
                variables: {
                  customer: row.customer,
                  project: row.project,
                  endDate: row.endDate,
                },
              });
            } else if (changedField === "receivedOn") {
              await updateProjectReceivedOn({
                variables: {
                  customer: row.customer,
                  project: row.project,
                  receivedOn: row.receivedOn,
                },
              });
            }
          }}
        />
      </Spin>
    </div>
  );
};
